import { useEffect } from "react";
import { addError } from "@jsq/observability";

const UNKNOWN = "(unknown error)";

/**
 * Formats an unknown thrown value into a non-empty string suitable for
 * `addError`.
 *
 * Why this exists: `JSON.stringify(error.cause)` is fragile. If `cause` is
 * `undefined` it returns the literal `undefined` (not a string); if `cause`
 * is an `Error` it returns `"{}"` because `Error` own-properties are
 * non-enumerable. Both produce useless Datadog entries.
 *
 * For plain objects we attempt safe JSON serialization to avoid the default
 * `[object Object]` from `String()`, which is the lowest-signal payload
 * Datadog can receive. Circular references, BigInt, etc. fall through to
 * `String()` so we still return *something*.
 */
export const formatError = (err: unknown): string => {
  if (err == null) return UNKNOWN;
  if (typeof err === "string") return err || UNKNOWN;
  if (err instanceof Error) return err.message || err.name || "Error";
  if (typeof err === "object") {
    try {
      const json = JSON.stringify(err);
      // JSON.stringify returns "{}" for objects with no enumerable own-props
      // (e.g. host objects, frozen prototypes) — fall through in that case.
      if (json && json !== "{}" && json !== "null") return json;
    } catch {
      // Circular references / unsupported types (e.g. BigInt) — fall through.
    }
  }
  return String(err) || UNKNOWN;
};

/**
 * Reports a render-time error condition to Datadog via `addError`.
 *
 * Pass `errorPayload` and it is reported whenever the value transitions to a
 * non-empty string. Pass `null` / `undefined` / `""` while there's nothing
 * to report.
 *
 * No cleanup is registered: `addError` is fire-and-forget and the
 * `@jsq/observability` SDK manages its own buffering/transport.
 *
 * For imperative reporting from event handlers / catch blocks, call
 * `addError` from `@jsq/observability` directly (typically wrapping the
 * thrown value with `formatError` from this module).
 *
 * @param errorPayload Optional. If provided and truthy, reported on change.
 */
export const useReportError = (errorPayload?: string | null): void => {
  useEffect(() => {
    if (errorPayload) {
      addError(errorPayload);
    }
  }, [errorPayload]);
};
